import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Switch, FormControlLabel, Avatar, Divider, Alert,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, PersonAdd as InviteIcon,
  Check as AcceptIcon, Close as DeclineIcon, ExitToApp as LeaveIcon,
  Business as OrgIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import organizationService, {
  Organization, OrganizationInvitation, OrganizationVisibility,
} from '../services/organization.service';

interface OrganizationTabProps {
  networkConnections: any[];
}

const OrganizationTab: React.FC<OrganizationTabProps> = ({ networkConnections }) => {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteOrgId, setInviteOrgId] = useState<number | null>(null);

  const loadData = async () => {
    try {
      const [o, i] = await Promise.all([
        organizationService.getMyOrganizations(),
        organizationService.getPendingInvitations(),
      ]);
      setOrgs(o);
      setInvitations(i);
    } catch (e) {
      console.error('Error loading organizations:', e);
    }
  };

  useEffect(() => { loadData(); }, []);


  const handleCreate = async () => {
    if (!newOrgName.trim()) return;
    try {
      await organizationService.createOrganization(newOrgName.trim());
      setNewOrgName('');
      setCreateOpen(false);
      toast.success('Organisation créée');
      loadData();
    } catch (e) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleAcceptInvitation = async (id: number) => {
    try {
      await organizationService.acceptInvitation(id);
      toast.success('Invitation acceptée');
      loadData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleDeclineInvitation = async (id: number) => {
    try {
      await organizationService.declineInvitation(id);
      toast.info('Invitation refusée');
      loadData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleInvite = async (userId: number) => {
    if (!inviteOrgId) return;
    try {
      await organizationService.inviteMember(inviteOrgId, userId);
      toast.success('Invitation envoyée');
      setInviteOpen(false);
      loadData();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Erreur');
    }
  };

  const handleRemoveMember = async (orgId: number, userId: number) => {
    try {
      await organizationService.removeMember(orgId, userId);
      toast.success('Membre retiré');
      loadData();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Erreur');
    }
  };

  const handleLeave = async (orgId: number) => {
    try {
      await organizationService.leaveOrganization(orgId);
      toast.success('Vous avez quitté l\'organisation');
      loadData();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Erreur');
    }
  };

  const handleToggleInvite = async (orgId: number, userId: number, canInvite: boolean) => {
    try {
      await organizationService.updateMemberRights(orgId, userId, { can_invite: canInvite });
      loadData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleToggleVisibility = async (permId: number, field: 'can_see_invoices' | 'can_see_timeslots', value: boolean) => {
    try {
      const updated = await organizationService.updateVisibility(permId, { [field]: value });
      // Update local state
      setOrgVisibilities(prev => {
        const newState = { ...prev };
        for (const orgId of Object.keys(newState)) {
          newState[Number(orgId)] = newState[Number(orgId)].map(p =>
            p.id === permId ? { ...p, [field]: value } : p
          );
        }
        return newState;
      });
    } catch (e) {
      toast.error('Erreur');
    }
  };

  // Get current user ID from auth store
  const [currentUserId, setCurrentUserId] = useState<number>(0);
  const [orgVisibilities, setOrgVisibilities] = useState<Record<number, OrganizationVisibility[]>>({});

  useEffect(() => {
    // Determine current user from memberships
    for (const org of orgs) {
      // We are in all these orgs, find our user id
      // Load visibility for all orgs
      organizationService.getVisibilitySettings(org.id).then((v) => {
        setOrgVisibilities(prev => ({ ...prev, [org.id]: v }));
        // Our user id is the granting_user on these perms
        if (v.length > 0) {
          setCurrentUserId(v[0].granting_user);
        }
      }).catch(() => {});
    }
  }, [orgs]);

  // Fallback: get current user from API
  useEffect(() => {
    if (currentUserId === 0) {
      import('../services/api').then(mod => {
        mod.default.get('/auth/profile/').then((res: any) => {
          setCurrentUserId(res.data.id);
        }).catch(() => {});
      });
    }
  }, [currentUserId]);

  return (
    <Box sx={{ px: 3, py: 2 }}>
      {/* Invitations pendantes */}
      {invitations.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Invitations reçues</Typography>
          {invitations.map((inv) => (
            <Card key={inv.id} sx={{ mb: 1, border: '1px solid', borderColor: 'warning.main' }}>
              <CardContent sx={{ py: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <OrgIcon color="warning" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight="bold">{inv.organization_name}</Typography>
                  <Typography variant="caption" color="text.secondary">Invité par {inv.invited_by_name}</Typography>
                </Box>
                <IconButton color="success" onClick={() => handleAcceptInvitation(inv.id)} title="Accepter">
                  <AcceptIcon />
                </IconButton>
                <IconButton color="error" onClick={() => handleDeclineInvitation(inv.id)} title="Refuser">
                  <DeclineIcon />
                </IconButton>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Mes organisations */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Mes organisations</Typography>
        <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => setCreateOpen(true)}>
          Créer
        </Button>
      </Box>

      {orgs.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          Vous ne faites partie d'aucune organisation.
        </Typography>
      )}

      {orgs.map((org) => {
        const myMembership = org.members.find(m => m.user === currentUserId) || org.members[0];
        const isAdmin = org.created_by === currentUserId || myMembership?.role === 'admin';
        return (
          <Card key={org.id} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <OrgIcon color="primary" />
                <Typography variant="h6" sx={{ flex: 1 }}>{org.name}</Typography>
                {isAdmin && (
                  <Chip label="Admin" color="primary" size="small" />
                )}
                {!isAdmin && (
                  <IconButton size="small" color="error" onClick={() => handleLeave(org.id)} title="Quitter">
                    <LeaveIcon />
                  </IconButton>
                )}
                {(isAdmin || myMembership?.can_invite) && (
                  <IconButton size="small" color="primary"
                    onClick={() => { setInviteOrgId(org.id); setInviteOpen(true); }}
                    title="Inviter">
                    <InviteIcon />
                  </IconButton>
                )}
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Members */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {org.members.map((member) => {
                  const isSelf = member.user === currentUserId;
                  const visPerms = orgVisibilities[org.id] || [];
                  // Find the permission where I grant visibility to this member
                  const myPermForThisMember = visPerms.find(p => p.viewing_user === member.user);

                  return (
                    <Card key={member.id} variant="outlined"
                      sx={{ minWidth: 200, flex: '1 1 200px', bgcolor: isSelf ? 'action.hover' : 'inherit' }}>
                      <CardContent sx={{ py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
                            {member.user_name?.charAt(0) || '?'}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {member.user_name || member.username}
                              {isSelf && ' (moi)'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {member.role === 'admin' ? 'Admin' : 'Membre'}
                            </Typography>
                          </Box>
                          {isAdmin && member.role !== 'admin' && !isSelf && (
                            <IconButton size="small" color="error" onClick={() => handleRemoveMember(org.id, member.user)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>

                        {/* Admin controls: can_invite toggle */}
                        {isAdmin && member.role !== 'admin' && !isSelf && (
                          <FormControlLabel
                            control={<Switch size="small" checked={member.can_invite}
                              onChange={(e) => handleToggleInvite(org.id, member.user, e.target.checked)} />}
                            label={<Typography variant="caption">Peut inviter</Typography>}
                            sx={{ mt: 0.5, ml: 0 }}
                          />
                        )}

                        {/* Visibility toggles: what I grant to this member */}
                        {!isSelf && myPermForThisMember && (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                              J'autorise à voir :
                            </Typography>
                            <FormControlLabel
                              control={<Switch size="small" checked={myPermForThisMember.can_see_invoices}
                                onChange={(e) => handleToggleVisibility(myPermForThisMember.id, 'can_see_invoices', e.target.checked)} />}
                              label={<Typography variant="caption">Factures</Typography>}
                              sx={{ ml: 0, mr: 1 }}
                            />
                            <FormControlLabel
                              control={<Switch size="small" checked={myPermForThisMember.can_see_timeslots}
                                onChange={(e) => handleToggleVisibility(myPermForThisMember.id, 'can_see_timeslots', e.target.checked)} />}
                              label={<Typography variant="caption">Créneaux</Typography>}
                              sx={{ ml: 0 }}
                            />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        );
      })}

      {/* Create org dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Créer une organisation</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Nom de l'organisation" value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)} sx={{ mt: 1 }}
            onKeyPress={(e) => e.key === 'Enter' && handleCreate()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Annuler</Button>
          <Button onClick={handleCreate} variant="contained" disabled={!newOrgName.trim()}>Créer</Button>
        </DialogActions>
      </Dialog>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Inviter un collègue</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sélectionnez un enseignant de votre réseau :
          </Typography>
          {networkConnections.map((conn: any) => (
            <Card key={conn.id} sx={{ mb: 1, cursor: 'pointer' }}
              onClick={() => handleInvite(conn.connected_user_id || conn.id)}>
              <CardContent sx={{ py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
                  {(conn.connected_user_name || conn.username || '?').charAt(0)}
                </Avatar>
                <Typography variant="body2">{conn.connected_user_name || conn.username}</Typography>
              </CardContent>
            </Card>
          ))}
          {networkConnections.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Aucun enseignant dans votre réseau. Ajoutez des collègues dans l'onglet Réseau.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Annuler</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationTab;
